import { Separator } from "@monbits/ui";
import {
	Ban,
	CheckCircle,
	CircleDot,
	Clock,
	ShieldX,
	XCircle,
} from "lucide-react";
import * as React from "react";
import { useOutletContext, useParams } from "react-router-dom";
import CopyButton from "src/components/copy-button/copy-button";
import DetailRow from "src/components/detail-row/detail-row";
import { EntityDrawer } from "src/components/entity-drawer";
import { StatusBadge } from "src/components/status-badge";
import type { OrdersOutletContext } from "./orders-layout";
export function OrderDetail() {
	const { orderId } = useParams<{ orderId: string }>();
	const outletContext = useOutletContext<OrdersOutletContext | undefined>();
	const orders = outletContext?.orders ?? [];
	const isLoading = outletContext?.isLoading ?? false;

	const order = React.useMemo(() => {
		if (!orderId) {
			return null;
		}
		return orders.find((item) => item.uuid === orderId) ?? null;
	}, [orders, orderId]);

	const isNotFound = !isLoading && (!orderId || order === null);

	const formatDate = (dateStr: string): string => {
		const date = new Date(dateStr);
		if (Number.isNaN(date.getTime())) {
			return dateStr;
		}

		return date.toLocaleDateString("en-US", {
			year: "numeric",
			month: "short",
			day: "numeric",
			hour: "2-digit",
			minute: "2-digit",
		});
	};

	const formatAmount = (value?: string | null): string => {
		if (!value) {
			return "0";
		}
		return value;
	};

	const getStatusConfig = (
		status: string,
	): {
		type: "success" | "warning" | "error" | "info" | "default" | "danger";
		icon: typeof CheckCircle;
		label: string;
	} => {
		switch (status) {
			case "paid":
				return { type: "success", icon: CheckCircle, label: "Paid" };
			case "partially_paid":
				return { type: "info", icon: CircleDot, label: "Partially Paid" };
			case "pending":
				return { type: "warning", icon: Clock, label: "Pending" };
			case "expired":
				return { type: "error", icon: XCircle, label: "Expired" };
			case "canceled":
			case "cancelled":
				return { type: "default", icon: Ban, label: "Cancelled" };
			case "aml_rejected":
				return { type: "danger", icon: ShieldX, label: "AML Rejected" };
			default:
				return { type: "default", icon: Clock, label: status };
		}
	};

	return (
		<EntityDrawer
			title="Order Details"
			isLoading={isLoading}
			isNotFound={isNotFound}
			notFoundMessage="Order not found"
			parentPath="/orders"
		>
			{order && (
				<div className="p-6 space-y-6">
					<div>
						<h3 className="text-sm font-semibold mb-3">Basic Information</h3>
						<div className="space-y-2 text-sm">
							<DetailRow
								label="Status"
								value={(() => {
									const config = getStatusConfig(order.status);
									return (
										<StatusBadge type={config.type} icon={config.icon}>
											{config.label}
										</StatusBadge>
									);
								})()}
							/>
							<DetailRow
								label="Test Mode"
								value={order.isTest ? "Yes" : "No"}
							/>
						</div>
					</div>

					<Separator />

					<div>
						<h3 className="text-sm font-semibold mb-3">Identifiers</h3>
						<div className="space-y-2 text-sm">
							<DetailRow
								label="UUID"
								value={
									<>
										<span className="font-mono text-xs break-all font-medium">
											{order.uuid}
										</span>
										<CopyButton value={order.uuid} label="Order UUID" />
									</>
								}
							/>
							<DetailRow
								label="Merchant Order"
								value={
									<>
										<span>{order.merchantOrderId}</span>
										<CopyButton
											value={order.merchantOrderId}
											label="Merchant order ID"
										/>
									</>
								}
							/>
							<DetailRow label="Wallet ID" value={order.walletId} />
						</div>
					</div>

					<Separator />

					<div>
						<h3 className="text-sm font-semibold mb-3">Amounts</h3>
						<div className="space-y-2 text-sm">
							<DetailRow
								label="Fiat Amount"
								value={`${formatAmount(order.fiatAmount)} ${order.fiatCurrency}`}
							/>
							<DetailRow
								label="Crypto Amount"
								value={`${formatAmount(order.cryptoAmount)} ${order.cryptoCurrency}`}
							/>
							<DetailRow
								label="Received Amount"
								value={`${formatAmount(order.receivedAmount)} ${order.cryptoCurrency}`}
							/>
							<DetailRow
								label="Service Fee (Expected)"
								value={`${formatAmount(order.serviceFeeExpected)} ${order.cryptoCurrency}`}
							/>
							<DetailRow
								label="Service Fee (Collected)"
								value={`${formatAmount(order.serviceFeeCollected)} ${order.cryptoCurrency}`}
							/>
						</div>
					</div>

					<Separator />

					<div>
						<h3 className="text-sm font-semibold mb-3">Payment Address</h3>
						<div className="flex items-start justify-between gap-2 text-sm">
							<span className="font-mono text-xs break-all">
								{order.paymentAddress}
							</span>
							<CopyButton value={order.paymentAddress} label="Address" />
						</div>
					</div>

					<Separator />

					<div>
						<h3 className="text-sm font-semibold mb-3">Merchant &amp; User</h3>
						<div className="space-y-2 text-sm">
							<DetailRow label="Merchant" value={order.merchantName} />
							{order.merchantUserName && (
								<DetailRow label="User Name" value={order.merchantUserName} />
							)}
							{order.merchantUserEmail && (
								<DetailRow label="User Email" value={order.merchantUserEmail} />
							)}
						</div>
					</div>

					<Separator />

					<div>
						<h3 className="text-sm font-semibold mb-3">Timestamps</h3>
						<div className="space-y-2 text-sm">
							<DetailRow label="Created" value={formatDate(order.createdAt)} />
							<DetailRow label="Updated" value={formatDate(order.updatedAt)} />
							<DetailRow label="Expires" value={formatDate(order.expiresAt)} />
						</div>
					</div>

					{order.description && (
						<>
							<Separator />
							<div>
								<h3 className="text-sm font-semibold mb-3">Description</h3>
								<p className="text-sm text-muted-foreground">
									{order.description}
								</p>
							</div>
						</>
					)}
				</div>
			)}
		</EntityDrawer>
	);
}
